import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  Grid2,
  Divider,
  Paper,
  CircularProgress,
} from '@mui/material';
import { useTestTalrooEndpointMutation } from '../../store/debug/debug.api';

const defaultJsonData = `{
  "id": "talroo-app-002",
  "initiator": "talroo-partner",
  "requestId": "req-2024-002-def456",
  "correlationId": "corr-abc123-2024",
  "type": "application_submit",
  "data": {
    "id": 98766,
    "job": {
      "city": "Dallas",
      "jobId": 54321,
      "state": "TX",
      "title": "Auto Mechanic",
      "company": "Quick Fix Auto",
      "zipcode": 75201,
      "jobReference": "QF-MECH-2024-002"
    },
    "applicant": {
      "city": "Dallas",
      "phone": "+1-555-987-6543",
      "state": "TX",
      "zipcode": "75201",
      "lastName": "24",
      "firstName": "jobseeker",
      "emailAddress": "jobseeker_24@carcareers.com",
      "resume": {
        "fileName": "jobseeker_24.pdf",
        "contentType": "application/pdf",
        "contentSha256": "b2c3d4e5f6789012345678901234567890abcdef1234567890abcdef1234567",
        "data": "JVBERi0xLjQKJcOkw7zDtsKQ..."
      }
    },
    "completed": false,
    "createdAt": "2024-01-16T14:20:00.000Z",
    "campaignId": 123,
    "applicationTime": "2024-01-16T14:15:00.000Z"
  },
  "createdAt": "2024-01-16T14:20:00.000Z"
}`;

export function RequestDebug() {
  const [postId, setPostId] = useState('63');
  const [source, setSource] = useState('Talroo');
  const [timestamp, setTimestamp] = useState('1718049653');
  const [signature, setSignature] = useState('f648d1ecc3bfc78ea0e37a637e8aa9be10554c4295d1ab6e989e1ac70411742b');
  const [jsonData, setJsonData] = useState(defaultJsonData);
  const [response, setResponse] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  const [testTalrooEndpoint, { isLoading }] = useTestTalrooEndpointMutation();

  const handleSubmit = async () => {
    setError(null);
    setResponse(null);

    try {
      JSON.parse(jsonData);
    } catch (err) {
      setError('Invalid JSON format in request body');
      return;
    }

    try {
      const result = await testTalrooEndpoint({
        postId,
        source,
        timestamp,
        signature,
        jsonData,
      }).unwrap();

      setResponse(result);
    } catch (err: any) {
      setError(err?.data?.message || err?.message || 'Request failed');
      setResponse(err?.data || err);
    }
  };

  const generateCurrentTimestamp = () => {
    const now = Math.floor(Date.now() / 1000);
    setTimestamp(now.toString());
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      <Typography variant="h4" gutterBottom>
        🔧 Talroo Endpoint Debugger
      </Typography>
      <Typography variant="body1" color="text.secondary" gutterBottom>
        Test the POST /partner/talroo/apply/:postId endpoint directly from the frontend
      </Typography>

      <Grid2 container spacing={3}>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Request Configuration
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  label="Post ID"
                  value={postId}
                  onChange={(e) => setPostId(e.target.value)}
                  size="small"
                  helperText="The job post ID to apply to"
                />

                <TextField
                  label="Source"
                  value={source}
                  onChange={(e) => setSource(e.target.value)}
                  size="small"
                  helperText="Query parameter: ?source=Talroo"
                />

                <Divider />

                <Typography variant="subtitle2" color="primary">
                  x-talroo-signature Header
                </Typography>

                <Box sx={{ display: 'flex', gap: 1, alignItems: 'end' }}>
                  <TextField
                    label="Timestamp"
                    value={timestamp}
                    onChange={(e) => setTimestamp(e.target.value)}
                    size="small"
                    sx={{ flexGrow: 1 }}
                    helperText="Unix timestamp"
                  />
                  <Button variant="outlined" size="small" onClick={generateCurrentTimestamp}>
                    Now
                  </Button>
                </Box>

                <TextField
                  label="Signature (v1)"
                  value={signature}
                  onChange={(e) => setSignature(e.target.value)}
                  size="small"
                  helperText="HMAC SHA256 signature"
                />

                <Typography variant="caption" color="text.secondary">
                  Header format: t={timestamp}, v1={signature}
                </Typography>

                <Divider />

                <TextField
                  label="Request Body (JSON)"
                  value={jsonData}
                  onChange={(e) => setJsonData(e.target.value)}
                  multiline
                  rows={15}
                  variant="outlined"
                  sx={{
                    '& .MuiInputBase-input': {
                      fontFamily: 'monospace',
                      fontSize: '0.875rem',
                    },
                  }}
                  helperText="JSON payload for the request body"
                />

                <Button variant="contained" onClick={handleSubmit} disabled={isLoading} size="large" sx={{ mt: 2 }}>
                  {isLoading ? (
                    <>
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                      Sending Request...
                    </>
                  ) : (
                    '🚀 Send POST Request'
                  )}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid2>

        <Grid2 size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Response
              </Typography>

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  <Typography variant="subtitle2">Error:</Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    {error}
                  </Typography>
                </Alert>
              )}

              {response && (
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    bgcolor: 'grey.50',
                    border: 1,
                    borderColor: 'grey.200',
                    maxHeight: 500,
                    overflow: 'auto',
                  }}
                >
                  <Typography variant="subtitle2" gutterBottom>
                    Response Data:
                  </Typography>
                  <pre
                    style={{
                      margin: 0,
                      fontSize: '0.875rem',
                      fontFamily: 'monospace',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}
                  >
                    {JSON.stringify(response, null, 2)}
                  </pre>
                </Paper>
              )}

              {!response && !error && (
                <Alert severity="info">Fill out the form and click "Send POST Request" to test the endpoint</Alert>
              )}
            </CardContent>
          </Card>
        </Grid2>
      </Grid2>

      <Box sx={{ mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          🎯 Testing Strategy
        </Typography>
        <Alert severity="info">
          <Typography variant="body2">
            <strong>Step 1:</strong> Use the default values first to test the exact same request that's failing in
            Postman
            <br />
            <strong>Step 2:</strong> Check browser network tab to see if the request is being sent correctly
            <br />
            <strong>Step 3:</strong> Compare the response with your server logs to identify where the request is being
            blocked
            <br />
            <strong>Step 4:</strong> If you get the same 404 error, it confirms the issue is at the proxy/infrastructure
            level
          </Typography>
        </Alert>
      </Box>
    </Box>
  );
}
